import { Link } from "react-router-dom";
import useScrollAnimation from "../../../hooks/useScrollAnimation";

const solutions = [
  {
    number: "01",
    title: "AI PATIENT ACQUISITION",
    icon: "ri-radar-line",
    bullets: [
      "24/7 intelligent lead capture across every channel",
      "Instant qualification and intent scoring",
      "Automated consultation booking without staff involvement",
    ],
  },
  {
    number: "02",
    title: "INTELLIGENT SCHEDULING",
    icon: "ri-calendar-check-line",
    bullets: [
      "Predictive availability management to eliminate gaps",
      "Real-time calendar sync with automated confirmations",
      "No-show reduction through smart reminder sequences",
    ],
  },
  {
    number: "03",
    title: "AUTOMATED FOLLOW-UP",
    icon: "ri-send-plane-line",
    bullets: [
      "Personalized nurture sequences triggered by patient behavior",
      "Multi-channel follow-up within 5 minutes of inquiry",
      "Qualification pipeline that filters and converts leads",
    ],
  },
  {
    number: "04",
    title: "RETENTION SYSTEMS",
    icon: "ri-shield-check-line",
    bullets: [
      "Lifecycle automation that re-engages lapsed patients",
      "Loyalty triggers based on visit frequency and spend",
      "Long-term relationship infrastructure built into operations",
    ],
  },
];

const SolutionSection = () => {
  const sectionRef = useScrollAnimation<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="bg-black py-24 md:py-36 px-6 md:px-16 border-t border-white/5 relative overflow-hidden"
    >
      {/* Abstract left glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: "-150px",
          top: "40%",
          transform: "translateY(-50%)",
          width: 400,
          height: 400,
          background: "radial-gradient(circle, rgba(255,209,0,0.05) 0%, transparent 70%)",
          borderRadius: "50%",
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="reveal">
            <span
              className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4"
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              ENGINEERED SYSTEMS
            </span>
            <h2
              className="text-white font-black uppercase text-4xl md:text-5xl leading-none"
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
            >
              WHAT WE BUILD
              <br />
              <span className="text-[#FFD100]">FOR YOU</span>
            </h2>
          </div>
          <div className="reveal delay-200 hidden md:block w-px h-16 bg-[#FFD100]/20 self-end mb-1" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {solutions.map((sol, i) => (
            <div
              key={sol.number}
              className={`reveal delay-${(i + 1) * 100} group relative bg-[#1A1A1A] border-l-2 border-[#FFD100] p-8 md:p-10 flex flex-col gap-6 cursor-pointer transition-all duration-300 hover:bg-[#222] overflow-hidden`}
            >
              {/* Background icon */}
              <div
                className="absolute right-6 top-6 opacity-[0.06] group-hover:opacity-[0.10] transition-opacity duration-300"
                style={{ fontSize: "5rem" }}
              >
                <i className={sol.icon} style={{ color: "#FFD100" }} />
              </div>

              {/* Number + Icon row */}
              <div className="flex items-center gap-4">
                <span
                  className="text-[#FFD100] text-sm font-black tracking-[3px]"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  {sol.number}
                </span>
                <div className="h-px flex-1 bg-[#FFD100]/15" />
              </div>

              {/* Title */}
              <h3
                className="text-white font-black text-xl md:text-2xl tracking-[0.5px] leading-tight"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {sol.title}
              </h3>

              {/* Divider */}
              <div className="w-12 h-px bg-[#FFD100]/40" />

              {/* Bullets */}
              <ul className="flex flex-col gap-3">
                {sol.bullets.map((b, bi) => (
                  <li
                    key={bi}
                    className="flex items-start gap-3 text-[#E5E5E5] text-sm"
                    style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.75" }}
                  >
                    <span className="text-[#FFD100] font-bold mt-0.5 flex-shrink-0">—</span>
                    {b}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <span
                  className="text-[#FFD100] text-xs font-black tracking-[2px] flex items-center gap-2 group-hover:gap-3 transition-all duration-300"
                  style={{ fontFamily: "Arial, sans-serif" }}
                >
                  LEARN MORE <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
